import {React, observer, computed, IDisposer, useEffect, classNames, useAsObservableSource, observable, action, Fuse} from '@api'
import {CircularProgress} from '@material-ui/core'
import {ChangeLogTitleBarItem, useVipLocalStore, VipLocalStore, useChangeLogFlexLayoutTabStore, ChangeLogFlexLayoutTabStore, ISortWithOrder} from '@modules'
import {icons, StoresInstance, useCatalog} from '@stores'
import {Sync} from '@material-ui/icons'
import {ChangeLogFilterIcon, ChangeLogSortIcon} from '@stores/ui'
import {PrimaryIconButton, SearchField2} from 'centric-ui-components'
import {autorun} from 'mobx'
import * as css from './ChangeLogTitleBar.css'
import {Switch} from '@blueprintjs/core'
import {ChangeLogToolbar} from '.'

/// region Store

export class ChangeLogTitleBarStore extends VipLocalStore {
  constructor(stores: StoresInstance, public root: ChangeLogFlexLayoutTabStore, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {root: {filterFields}} = this
        this.fuse = new Fuse(filterFields, {keys: ['label']})
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @computed get filterLabel() {
    const {filterCount, filters, catalog: {labelForField}} = this
    if (!filterCount) {
      return 'Filter'
    }
    if (filterCount > 3) {
      return `Filters (${filterCount})`
    }
    return `Filters: ${Object.keys(filters).reduce((acc, key) => {
      const filter = filters[key]
      return [
        ...acc,
        labelForField(filter.field) ?? filter.field.label
      ]
    }, []).join(', ')}`
  }

  @computed get sortLabel() {
    const {sortCount, sortWithOrder, catalog: {labelForField}} = this
    if (!sortCount) {
      return 'Sort'
    }
    if (sortCount > 3) {
      return `Sort (${sortCount})`
    }
    return `Sort: ${sortWithOrder.map((sortItem) => {
      if (sortItem.field) {
        return `${labelForField(sortItem.field)} ${sortItem.order === 'asc' ? '&uarr;' : '&darr;'}`
      }
      return ''
    }).join(', ')}`
  }

  @computed get filters() {
    const {root: {filters}} = this
    return filters
  }

  @computed get filterCount() {
    const {filters} = this

    return Object.keys(filters).length

    /*return Object.keys(filters).reduce((acc, key) => {
      const filter = filters[key]
      acc += (filter.selected?.value ?? filter.selected ?? []).length ? 1 : 0
      return acc
    }, 0)*/
  }

  @computed get sortWithOrder(): Array<ISortWithOrder> {
    const {root: {sortWithOrder}} = this
    return sortWithOrder
  }

  @computed get sortCount() {
    const {sortWithOrder} = this
    return sortWithOrder.length
  }

  @observable fuse

  @observable searchText: string = null

  @action onSearch = (e) => {
    this.searchText = e.target.value
  }

  @computed get values() {
      const {root: {filterFields}, searchText, fuse} = this

      return searchText ? fuse.search(searchText).map(f => f.item) : filterFields
  }


}

export const ChangeLogTitleBarStoreContext = React.createContext<ChangeLogTitleBarStore>(null)
export const useChangeLogTitleBarStore = () => React.useContext(ChangeLogTitleBarStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const ChangeLogTitleBar = observer(function ChangeLogTitleBar(props) {
  const {newLook} = useChangeLogFlexLayoutTabStore()

  return newLook ? <ChangeLogToolbar {...props} /> : <ChangeLogTitleBar_old {...props} />

})

export const ChangeLogTitleBar_old = observer<MyProps>(function ChangeLogToolbar({className, ...props}) {
  const rootStore = useChangeLogFlexLayoutTabStore(),
    obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ChangeLogTitleBarStore(stores, rootStore, obsProps)),
    {filterLabel, sortLabel, filterCount, sortCount} = store,
    {
      onClickSortMenuItem, onClickClearAllFilters, searchText, onSearch, toggleNewLook, newLook,
      catalog: {settings: {changelog: settings}}
    } = rootStore

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogTitleBarStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <div className={css.left}>
          <SearchField2
            className={css.search}
            smallSearchInput
            active
            value={searchText}
            loading={false}
            onSearch={onSearch}
            placeholder={`Search Change Log...`}/>
          <div className={css.chips}>
            <ChangeLogTitleBarItem title={filterLabel} applied={!!filterCount} chipIcon={<ChangeLogFilterIcon/>} onClear={onClickClearAllFilters} feature={'filter'}/>
            <ChangeLogTitleBarItem title={sortLabel} applied={!!sortCount} chipIcon={<ChangeLogSortIcon/>} onClickMenuItem={onClickSortMenuItem} feature={'sort'}/>
          </div>
        </div>
        <div className={css.right}>
          {DEBUG && <Switch className={css.featureToggle} checked={newLook} onChange={() => toggleNewLook()} label='New Look?'/>}
          {rootStore.loading ? <CircularProgress size={20} color={'secondary'} className={css.icon}/> : <PrimaryIconButton
            icon={<Sync/>}
            data-type={'reload'}
            tooltip={'Reload'}
            onClick={rootStore.refetch}
            className={css.icon}
          />}
          <PrimaryIconButton
            icon={icons.openInNew}
            className={css.icon}
            tooltip={'Download excel'}
            onClick={rootStore.exportExcel}
          />
          <PrimaryIconButton
            icon={icons.smsOption}
            className={css.icon}
          />
        </div>
      </div>
    </ChangeLogTitleBarStoreContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/changelog/toolbar/ChangeLogTitleBar.tsx